import { Link } from 'react-router-dom';
import { Minus, Plus, Trash2 } from 'lucide-react';
import { Sheet, SheetContent } from './ui/sheet';
import { Button } from './ui/button';
import { useCart } from '../contexts/CartContext';
import { formatPrice } from '../lib/utils';

export default function CartDrawer() {
  const { items, isOpen, setIsOpen, updateQty, remove, totals } = useCart();

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetContent side="right" className="flex flex-col p-0" data-testid="cart-drawer">
        <div className="px-8 pt-8 pb-4 border-b border-border">
          <p className="overline text-muted-foreground">Your Bag</p>
          <h2 className="mt-2 font-display text-3xl">{items.length === 0 ? 'Empty for now' : `${items.length} ${items.length === 1 ? 'piece' : 'pieces'}`}</h2>
        </div>

        <div className="flex-1 overflow-y-auto px-8 py-6">
          {items.length === 0 ? (
            <div className="mt-12 text-center">
              <p className="text-muted-foreground mb-8">Nothing here yet — begin with something considered.</p>
              <Button asChild variant="outline" onClick={() => setIsOpen(false)} data-testid="cart-empty-shop-cta">
                <Link to="/shop">Shop Everything</Link>
              </Button>
            </div>
          ) : (
            <ul className="divide-y divide-border">
              {items.map((item) => (
                <li key={item.key} className="py-6 flex gap-4" data-testid={`cart-item-${item.slug}`}>
                  <Link to={`/product/${item.slug}`} onClick={() => setIsOpen(false)} className="w-20 aspect-[3/4] bg-muted overflow-hidden shrink-0">
                    <img src={item.image} alt={item.name} className="h-full w-full object-cover" />
                  </Link>
                  <div className="flex-1 flex flex-col">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <Link to={`/product/${item.slug}`} onClick={() => setIsOpen(false)} className="text-sm link-underline">{item.name}</Link>
                        <p className="mt-1 overline text-muted-foreground">
                          {item.color?.name} · Size {item.size}
                        </p>
                      </div>
                      <p className="text-sm tabular whitespace-nowrap">{formatPrice(item.price * item.quantity)}</p>
                    </div>
                    <div className="mt-auto flex items-center justify-between pt-3">
                      <div className="inline-flex items-center border border-border">
                        <button aria-label="Decrease quantity" data-testid={`cart-qty-dec-${item.slug}`} className="h-8 w-8 inline-flex items-center justify-center hover:bg-foreground/5" onClick={() => updateQty(item.key, item.quantity - 1)}>
                          <Minus className="h-3 w-3" />
                        </button>
                        <span className="w-8 text-center text-sm tabular" data-testid={`cart-qty-${item.slug}`}>{item.quantity}</span>
                        <button aria-label="Increase quantity" data-testid={`cart-qty-inc-${item.slug}`} className="h-8 w-8 inline-flex items-center justify-center hover:bg-foreground/5" onClick={() => updateQty(item.key, item.quantity + 1)}>
                          <Plus className="h-3 w-3" />
                        </button>
                      </div>
                      <button aria-label="Remove item" data-testid={`cart-remove-${item.slug}`} className="text-muted-foreground hover:text-foreground transition-colors duration-500" onClick={() => remove(item.key)}>
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {items.length > 0 && (
          <div className="border-t border-border px-8 py-6 space-y-4">
            <div className="flex items-baseline justify-between">
              <span className="overline text-muted-foreground">Subtotal</span>
              <span className="font-display text-2xl tabular" data-testid="cart-subtotal">{formatPrice(totals.subtotal)}</span>
            </div>
            {totals.couponDiscount > 0 && (
              <div className="flex items-baseline justify-between overline text-muted-foreground">
                <span>Discount</span>
                <span className="tabular">− {formatPrice(totals.couponDiscount)}</span>
              </div>
            )}
            <div className="flex items-baseline justify-between overline text-muted-foreground">
              <span>Shipping</span>
              <span className="tabular">{totals.shipping === 0 ? 'Complimentary' : formatPrice(totals.shipping)}</span>
            </div>
            <p className="overline text-muted-foreground">Taxes and any coupon calculated at bag.</p>
            <Button className="w-full" size="lg" asChild data-testid="cart-checkout-btn">
              <Link to="/cart" onClick={() => setIsOpen(false)}>Review your bag</Link>
            </Button>
            <Button asChild variant="ghost" className="w-full" onClick={() => setIsOpen(false)} data-testid="cart-view-bag">
              <Link to="/shop">Continue shopping</Link>
            </Button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
